import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/ui/Layout';
import { SITE_URL, SITE_NAME, getProductShareUrl, getProductShareSlug } from '../../constants/site';
import ShareButton from '../../components/ui/ShareButton';
import { db } from '../../firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, getDocs, getDoc, where, updateDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Form, Button, InputGroup, Container, Row, Col, Card, Alert, Modal } from 'react-bootstrap';
import { useAuth } from '../../components/context/AuthContext';
import NotificationPermission from '../../components/ui/NotificationPermission';
import { createLogger } from '../../utils/logger';
import { isIOSOrMobileSafari } from '../../utils/browserUtils';

const logger = createLogger('Welcome');

// Mobile-responsive styles for the name change modal
const mobileModalStyles = `
  .name-change-modal .modal-dialog {
    margin: 1rem;
    max-width: 400px;
  }
  
  .name-change-modal .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  .name-change-modal .modal-header {
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
  }
  
  .name-change-modal .modal-title {
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  .name-change-modal .modal-body {
    padding: 1.5rem;
  }
  
  .name-change-modal .modal-footer {
    border-top: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
  }
  
  .name-input-field {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  
  .name-input-field:focus {
    border-color: #f38118;
    box-shadow: 0 0 0 0.2rem rgba(243, 129, 24, 0.25);
  }
  
  /* Mobile-specific adjustments */
  @media (max-width: 576px) {
    .name-change-modal .modal-dialog {
      margin: 0.5rem;
      max-width: calc(100vw - 1rem);
    }
    
    .name-change-modal .modal-header,
    .name-change-modal .modal-body,
    .name-change-modal .modal-footer {
      padding: 1rem;
    }
    
    .name-change-modal .modal-title {
      font-size: 1rem;
    }
    
    .name-input-field {
      font-size: 16px !important; /* Prevents zoom on iOS */
      width: 100% !important; /* Ensure full width on mobile */
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
    
    .name-change-modal .btn {
      font-size: 14px;
      padding: 8px 12px;
    }
  }
  
  /* Extra small screens */
  @media (max-width: 375px) {
    .name-change-modal .modal-dialog {
      margin: 0.25rem;
      max-width: calc(100vw - 0.5rem);
    }
    
    .name-change-modal .modal-header,
    .name-change-modal .modal-body,
    .name-change-modal .modal-footer {
      padding: 0.75rem;
    }
  }
`;

// Inject styles into the document head with duplicate prevention
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('mobile-modal-styles');
  if (!existingStyle) {
    const styleElement = document.createElement('style');
    styleElement.id = 'mobile-modal-styles';
    styleElement.textContent = mobileModalStyles;
    document.head.appendChild(styleElement);
  }
}

const SupermarketImage = ({ imagePath, altText }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:')) {
      const imageRef = ref(storage, imagePath);
      getDownloadURL(imageRef).then(setImageUrl)
        .catch(err => logger.error("[SupermarketImage] Error fetching image URL for path:", imagePath, err));
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:')) {
        logger.warn("[SupermarketImage] Attempted to use a blob URL. Path:", imagePath);
        setImageUrl(''); 
    } else if (imagePath) {
        logger.warn("[SupermarketImage] imagePath is not a string or is invalid:", imagePath);
        setImageUrl('');
    }
  }, [imagePath, storage]);
  if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:') && !imageUrl) {
    return <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Blob Preview</span></div>;
  } 
  if (!imageUrl) return <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Loading...</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{height: '200px', objectFit: 'cover'}}/>;
};

const ProductImage = ({ imagePath, altText }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:')) {
      const imageRef = ref(storage, imagePath);
      getDownloadURL(imageRef).then(setImageUrl)
        .catch(err => setImageUrl(''));
    } else {
      setImageUrl('');
    }
  }, [imagePath, storage]);
  if (!imageUrl) return <div className="card-img-top" style={{height: '160px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>No Image</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{height: '160px', objectFit: 'cover'}}/>;
};

export default function Welcome() {
  const [supermarkets, setSupermarkets] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cartAlert, setCartAlert] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [showNameModal, setShowNameModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [nameUpdateMessage, setNameUpdateMessage] = useState('');
  const cartListenerRef = useRef(null);
  const cartTimeoutRef = useRef(null);
  const { currentUser, userRole, userName, userDocId, updateUserName } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const supermarketsRef = collection(db, 'Supermarkets');
    const q = query(supermarketsRef, orderBy('display_name'), limit(6));
    const unsubscribe = onSnapshot(q, snapshot => {
      const fetchedSupermarkets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSupermarkets(fetchedSupermarkets);
    });
    return () => unsubscribe();
  }, []);

  // Listen for cart changes for users and employees
  useEffect(() => {
    if (!currentUser || !['User', 'Employee'].includes(userRole)) return;

    const cartRef = doc(db, 'Carts', currentUser.uid);
    cartListenerRef.current = onSnapshot(cartRef, doc => {
      if (doc.exists) {
        const cartData = doc.data();
        // If cart is packed, do not show alert
        if (cartData && cartData.status === 'packed_awaiting_pickup') {
          setCartAlert(null);
          if (cartTimeoutRef.current) clearTimeout(cartTimeoutRef.current);
          return;
        }
        // Clear any previous timeout
        if (cartTimeoutRef.current) clearTimeout(cartTimeoutRef.current);

        // Only show alert if there are products to pack
        const products = cartData && cartData.products ? cartData.products : [];
        const hasItemsToPack = Array.isArray(products) && products.some(p => p.quantity > 0);

        if (userRole === 'Employee' && hasItemsToPack) {
          setCartAlert(`A new cart is ready to be packed. (${new Date().toLocaleTimeString()})`);
        } else if (userRole !== 'Employee') {
          setCartAlert(`Your cart has been updated. (${new Date().toLocaleTimeString()})`);
        } else {
          setCartAlert(null);
        }
        cartTimeoutRef.current = setTimeout(() => setCartAlert(null), 4000);
      }
    });

    return () => {
      if (cartListenerRef.current) cartListenerRef.current();
      if (cartTimeoutRef.current) clearTimeout(cartTimeoutRef.current);
    };
  }, [currentUser, userRole]);

  // Optimized product fetching with better caching and reduced database calls
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        // Step 1: Get featured products (limit to 16 for perfect 4-column layout)
        const productsRef = collection(db, 'Products');
        const productsQuery = query(productsRef, limit(16));
        const productsSnapshot = await getDocs(productsQuery);
        const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Step 2: Get all inventory items for these products in a single query
        const productIds = products.map(p => p.id);
        
        // Only proceed with inventory query if we have products
        if (productIds.length === 0) {
          setProductsByCategory({});
          return;
        }
        
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('product_id', 'in', productIds),
          where('quantity', '>', 0)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        
        // Step 3: Group inventory by product and get unique supermarket IDs
        const inventoryByProduct = {};
        const supermarketIds = new Set();
        
        inventorySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const productId = data.product_id;
          const supermarketId = data.supermarket_id;
          
          if (!inventoryByProduct[productId]) {
            inventoryByProduct[productId] = [];
          }
          inventoryByProduct[productId].push(supermarketId);
          supermarketIds.add(supermarketId);
        });

        // Step 4: Fetch all supermarket details in a single batch
        const supermarketDetails = {};
        const supermarketPromises = Array.from(supermarketIds).map(async (id) => {
          try {
            const supermarketDoc = await getDoc(doc(db, 'Supermarkets', id));
            if (supermarketDoc.exists()) {
              supermarketDetails[id] = { id: supermarketDoc.id, ...supermarketDoc.data() };
            }
          } catch (error) {
            logger.error(`Error fetching supermarket ${id}:`, error);
          }
        });
        
        await Promise.all(supermarketPromises);

        // Step 5: Group products by category and attach supermarket data
        const grouped = {};
        const productsWithSupermarkets = [];
        
        for (const product of products) {
          const category = product.category || 'Uncategorized';
          if (!grouped[category]) grouped[category] = [];
          
          // Get supermarkets for this product (limit to 2 for performance)
          const productSupermarkets = inventoryByProduct[product.id] || [];
          const supermarketsForProduct = productSupermarkets
            .slice(0, 2) // Limit to 2 supermarkets per product
            .map(id => supermarketDetails[id])
            .filter(Boolean); // Remove any undefined entries
          
          // Include products even if they only have inventory at one supermarket
          // This ensures products are shown even when there's only one supermarket
          if (supermarketsForProduct.length > 0) {
            const productWithSupermarkets = {
              ...product,
              supermarketsForProduct
            };
            
            grouped[category].push(productWithSupermarkets);
            productsWithSupermarkets.push(productWithSupermarkets);
          }
        }

        // Show all products up to 16, don't force divisibility by 4
        const maxProducts = Math.min(16, productsWithSupermarkets.length);
        const finalProducts = productsWithSupermarkets.slice(0, maxProducts);
        
        // Rebuild grouped object with only the final products
        const finalGrouped = {};
        finalProducts.forEach(product => {
          const category = product.category || 'Uncategorized';
          if (!finalGrouped[category]) finalGrouped[category] = [];
          finalGrouped[category].push(product);
        });

        setProductsByCategory(finalGrouped);
      } catch (error) {
        logger.error('Error fetching products:', error);
        setProductsByCategory({});
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // Unique categories from featured products (for filter dropdown)
  const categories = React.useMemo(() => {
    const keys = Object.keys(productsByCategory);
    return ['', ...keys.sort((a, b) => (a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)))];
  }, [productsByCategory]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleOpenNameModal = () => {
    setNewName(userName || '');
    setShowNameModal(true);
    setNameUpdateMessage('');
  };

  const handleCloseNameModal = () => {
    setShowNameModal(false);
    setNewName('');
    setNameUpdateMessage('');
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      setNameUpdateMessage('Please enter a valid name');
      return;
    }

    if (newName.trim() === userName) {
      setNameUpdateMessage('This is already your current name');
      return;
    }

    if (!userDocId) {
      setNameUpdateMessage('Error: Unable to update name. Please try logging out and back in.');
      return;
    }

    setIsUpdatingName(true);
    setNameUpdateMessage('');

    try {
      const userDocRef = doc(db, 'Users', userDocId);
      await updateDoc(userDocRef, {
        first_name: newName.trim(),
        updated_at: Timestamp.fromDate(new Date())
      });

      // Update the AuthContext state immediately for better UX
      updateUserName(newName.trim());
      setNameUpdateMessage('Name updated successfully!');
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowNameModal(false);
        setNewName('');
        setNameUpdateMessage('');
      }, 1500);

    } catch (error) {
      logger.error('Error updating name:', error);
      setNameUpdateMessage('Failed to update name. Please try again.');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const seoTitle = `${SITE_NAME} — hardware, tools, and supplies online`;
  const seoDescription = `Shop hardware and supplies from trusted sellers. Compare items, check availability, and checkout with ${SITE_NAME}.`;
  const seoUrl = SITE_URL + '/';
  const seoImage = `${SITE_URL}/pack-my-hardware-logo.png`;

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={`hardware, tools, supplies, online store, ${SITE_NAME}`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>
      <Container className="mt-4">
        {/* Cart Notification Alert */}
        {cartAlert && (
          <Alert variant="info" className="mb-3 text-center" style={{position: 'sticky', top: 0, zIndex: 1050}}>
            {cartAlert}
          </Alert>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <img
            src="/pack-my-hardware-logo.png"
            alt={`${SITE_NAME} logo`}
            style={{
              maxWidth: 'min(320px, 100%)',
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
        </div>
        <h1 className="text-center mt-2 mb-3" style={{ fontWeight: 600 }}>
          Welcome to {SITE_NAME}!
        </h1>
        {currentUser && (
          <div className="text-center mb-3">
            <p className="text-muted mb-2" style={{ fontSize: '1.1rem' }}>
              Hello, {userName || currentUser.displayName || currentUser.email || 'User'}! 👋
            </p>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleOpenNameModal}
              style={{ fontSize: '0.9rem' }}
            >
              <i className="fas fa-edit me-1"></i>
              Change Name
            </Button>
          </div>
        )}

        {/* FCM Notification Permission for logged-in users - skip on iOS Safari (limited FCM support, can cause blank screen) */}
        {currentUser && !isIOSOrMobileSafari() && (
          <div className="notification-permission-wrapper">
            <NotificationPermission />
          </div>
        )}

        {/* Login Button for non-authenticated users */}
        {!currentUser && (
          <Row className="justify-content-center mb-4">
            <Col xs={12} className="text-center">
              <Button 
                variant="success" 
                size="lg"
                onClick={() => navigate('/login')}
                className="px-4 py-2"
                style={{ 
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <i className="fas fa-sign-in-alt me-2"></i>
                Login to Start Shopping
              </Button>
              <p className="text-muted mt-2 mb-0">
                Create an account or sign in to add items to your cart
              </p>
            </Col>
          </Row>
        )}

        <Row className="justify-content-center mb-4">
          <Col md={8} lg={6}>
            <Form onSubmit={handleSearchSubmit}>
              <InputGroup className="mb-3">
                <Form.Control
                  type="search"
                  placeholder="Search for hardware stores or products..."
                  aria-label="Search query"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="welcome-search-input" 
                />
                <Button type="submit" className="welcome-search-button">
                  Search
                </Button>
              </InputGroup>
            </Form>
          </Col>
        </Row>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-0 text-dark-green">Featured hardware stores</h2>
          <Link
            to="/hardware"
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            style={{ fontSize: '0.9rem' }}
          >
            <span>See all hardware stores</span>
            <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
        {supermarkets.length === 0 && !searchQuery && <p>Loading featured hardware stores...</p>}
        <Row xs={1} md={2} lg={3} className="g-4">
          {supermarkets.map(supermarket => (
            <Col key={supermarket.id}>
              <Card className="h-100 supermarket-card-custom">
                <Link to={`/hardware/${supermarket.id}/products`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {supermarket.media && supermarket.media.length > 0 && supermarket.media[0].url ? (
                    <SupermarketImage 
                      imagePath={supermarket.media[0].url} 
                      altText={supermarket.display_name} 
                    />
                  ) : (
                    <div className="card-img-top d-flex align-items-center justify-content-center" style={{height: '200px', backgroundColor: '#e9ecef'}}>
                      <span>No Image</span>
                    </div>
                  )}
                  <Card.Body>
                    <Card.Title className="text-center">{supermarket.display_name || 'Unnamed hardware store'}</Card.Title>
                    {supermarket.address && (
                      <Card.Text className="text-center text-muted" style={{ fontSize: '0.95em' }}>
                        {supermarket.address}
                      </Card.Text>
                    )}
                  </Card.Body>
                </Link>
                <Card.Footer className="bg-transparent border-top-0 pt-0">
                    <div className="d-flex justify-content-center">
                      <ShareButton
                        url={supermarket.seo_name ? `${SITE_URL}/hardware/${supermarket.id}/products/${supermarket.seo_name}` : `${SITE_URL}/hardware/${supermarket.id}/products`}
                      title={`${supermarket.display_name} - Products | ${SITE_NAME}`}
                      text={`Shop hardware from ${supermarket.display_name}`}
                      variant="outline-secondary"
                      size="sm"
                      showLabel={false}
                    />
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Products */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 mt-5 gap-2">
          <h2 className="mb-0 text-dark-green">Featured Products</h2>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {Object.keys(productsByCategory).length > 0 && (
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
                style={{ fontSize: '16px', width: 'auto', minWidth: '180px' }}
              >
                <option value="">All categories</option>
                {categories.filter(c => c !== '').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Select>
            )}
            <Link 
              to="/products" 
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              style={{ fontSize: '0.9rem' }}
            >
              <span>See All Products</span>
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
        {isLoadingProducts && <p>Loading featured products...</p>}
        {!isLoadingProducts && Object.keys(productsByCategory).length === 0 && (
          <p>No featured products available at the moment.</p>
        )}
        {(() => {
          // Flatten all products with their categories for compact layout (optionally filter by category)
          const allProducts = [];
          const entries = categoryFilter
            ? [[categoryFilter, productsByCategory[categoryFilter] || []]]
            : Object.entries(productsByCategory);
          entries.forEach(([category, products]) => {
            const visibleProducts = (products || []).filter(
              product => product.supermarketsForProduct && product.supermarketsForProduct.length > 0
            );
            visibleProducts.forEach(product => {
              allProducts.push({ ...product, category });
            });
          });

          if (allProducts.length === 0 && categoryFilter) {
            return <p className="text-muted">No featured products in this category.</p>;
          }

          // Group products into rows of 4 (or 3 on medium screens, 2 on small, 1 on xs)
          const productsPerRow = 4;
          const productRows = [];
          for (let i = 0; i < allProducts.length; i += productsPerRow) {
            productRows.push(allProducts.slice(i, i + productsPerRow));
          }

          return (
            <div>
              {productRows.map((row, rowIndex) => (
                <Row key={rowIndex} xs={1} sm={2} md={3} lg={4} className="g-4 mb-4">
                  {row.map(product => (
                    <Col key={product.id}>
                      <Card className="h-100">
                        <Link to={`/product/${getProductShareSlug(product)}`} className="d-block text-decoration-none" style={{ color: 'inherit' }}>
                          {product.media && product.media.length > 0 && product.media[0].url ? (
                            <ProductImage imagePath={product.media[0].url} altText={product.display_name} />
                          ) : (
                            <div className="card-img-top d-flex align-items-center justify-content-center" style={{height: '160px', backgroundColor: '#f0f0f0'}}>
                              <span>No Image</span>
                            </div>
                          )}
                        </Link>
                        <Card.Body>
                          <Card.Title className="text-center">{product.display_name || 'Unnamed Product'}</Card.Title>
                          <div className="mt-2">
                            <div className="mb-1" style={{fontSize: '0.95em'}}>Available at:</div>
                            {product.supermarketsForProduct.slice(0,2).map(sup => (
                              <div key={sup.id}>
                                <Link
                                  to={`/hardware/${sup.id}/products/${getProductShareSlug(product)}`}
                                  style={{textDecoration: 'underline', color: '#1a7f37'}}
                                >
                                  {sup.display_name || 'Hardware store'}
                                </Link>
                              </div>
                            ))}
                          </div>
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-top-0 pt-0">
                          <div className="d-flex justify-content-center">
                            <ShareButton
                              url={getProductShareUrl(product)}
                              title={`${product.display_name} | ${SITE_NAME}`}
                              text={`Check out ${product.display_name} on ${SITE_NAME}`}
                              variant="outline-secondary"
                              size="sm"
                              showLabel={false}
                            />
                          </div>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ))}
            </div>
          );
        })()}

        {/* See All Products Button - Bottom */}
        {!isLoadingProducts && Object.keys(productsByCategory).length > 0 && (
          <Row className="justify-content-center mt-4 mb-5">
            <Col xs="auto">
              <Link 
                to="/products" 
                className="btn btn-primary btn-lg px-4 py-2"
                style={{ 
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <i className="fas fa-shopping-bag me-2"></i>
                Browse All Products
              </Link>
            </Col>
          </Row>
        )}

        {/* Change Name Modal */}
        <Modal 
          show={showNameModal} 
          onHide={handleCloseNameModal} 
          centered
          size="sm"
          className="name-change-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="w-100 text-center">
              <i className="fas fa-user-edit me-2"></i>
              Change Your Name
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Your Name</Form.Label>
                <Form.Control
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={50}
                  disabled={isUpdatingName}
                  className="name-input-field"
                  style={{
                    fontSize: '16px', // Prevents zoom on iOS
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                    width: '100%' // Ensure full width
                  }}
                />
                <Form.Text className="text-muted mt-2 d-block">
                  This name will be displayed throughout the application.
                </Form.Text>
              </Form.Group>
              
              {nameUpdateMessage && (
                <Alert 
                  variant={nameUpdateMessage.includes('successfully') ? 'success' : 'danger'}
                  className="mb-3 text-center"
                  style={{ fontSize: '14px' }}
                >
                  {nameUpdateMessage}
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer className="px-4 py-3">
            <div className="w-100 d-flex gap-2">
              <Button 
                variant="secondary" 
                onClick={handleCloseNameModal}
                disabled={isUpdatingName}
                className="flex-fill"
                style={{
                  padding: '10px 16px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpdateName}
                disabled={isUpdatingName || !newName.trim()}
                className="flex-fill"
                style={{
                  padding: '10px 16px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                {isUpdatingName ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Update Name
                  </>
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
}
